import { NvoqMessage } from './NvoqQueue/createNvoqWebsocketFactory';
type StableTextMarker = NonNullable<NvoqMessage['data']['markers']>[number];

export const wordCount = (s: string): number =>
  s
    .trim()
    .split(' ')
    .filter((str) => str.length > 0).length;

/**
 * ASRPlexDictation will be used if either isFocusModeOn or asrPlexFeatureEnabled is true.
 * Otherwise, NvoqQueueProvider will be used.
 */

export const checkShouldUseASRPlex = ({
  isFocusModeOn,
  asrPlexFeatureEnabled,
}: {
  isFocusModeOn: boolean;
  asrPlexFeatureEnabled: boolean;
}) => {
  return isFocusModeOn || asrPlexFeatureEnabled;
};

export const getMarkerStartMs = (marker: StableTextMarker): number => {
  if (typeof marker.audioStart === 'number') {
    return marker.audioStart;
  }
  if (typeof marker.time_start === 'number') {
    return marker.time_start * 1000;
  }
  return 0;
};

export const getMarkerEndMs = (marker: StableTextMarker): number => {
  if (typeof marker.audioLength === 'number') {
    return getMarkerStartMs(marker) + marker.audioLength;
  }
  if (typeof marker.time_end === 'number') {
    return marker.time_end * 1000;
  }
  return getMarkerStartMs(marker);
};

export const getBaseDictationId = (id: string): string => id.replace(/-\d+$/, '');

export const isNumericSegmentForBaseId = (key: string, baseId: string): boolean => {
  const sequencePrefix = `${baseId}-`;
  if (!key.startsWith(sequencePrefix)) return false;
  const remainder = key.slice(sequencePrefix.length);
  return /^\d+$/.test(remainder);
};

/**
 * Resolve the "dictation family" id for finalize/stop flows.
 * We only strip a trailing -<digits> when that id is actually part of a modify-dictation family.
 * This protects real dictation ids like "foo-1" from being incorrectly normalized to "foo".
 */
export const resolveDictationFamilyId = (
  id: string,
  dictationMap: ReadonlyMap<string, unknown>,
  isReporterModifyDictationEnabled: boolean
): string => {
  if (!isReporterModifyDictationEnabled) return id;

  const candidateBaseId = getBaseDictationId(id);
  if (candidateBaseId === id) return id;

  if (dictationMap.has(candidateBaseId)) return candidateBaseId;

  const hasSiblingNumericSegmentsForCandidate = Array.from(dictationMap.keys()).some(
    (key) => key !== id && isNumericSegmentForBaseId(key, candidateBaseId)
  );
  return hasSiblingNumericSegmentsForCandidate ? candidateBaseId : id;
};

export const getDictationSequenceRank = (key: string, baseId: string): number => {
  if (key === baseId) return 0;
  if (!isNumericSegmentForBaseId(key, baseId)) return -1;
  const remainder = key.slice(`${baseId}-`.length);
  return Number.parseInt(remainder, 10) + 1;
};

export const getSortedDictationFamilyEntries = <T extends { audioOffsetMs?: number }>(
  id: string,
  dictationMap: ReadonlyMap<string, T>,
  isReporterModifyDictationEnabled: boolean
): {
  baseId: string;
  familyEntries: Array<[string, T]>;
} => {
  const baseId = resolveDictationFamilyId(id, dictationMap, isReporterModifyDictationEnabled);
  const familyEntries = Array.from(dictationMap.entries())
    .filter(([key]) => key === baseId || isNumericSegmentForBaseId(key, baseId))
    .sort((a, b) => {
      const offsetDiff = (a[1].audioOffsetMs ?? 0) - (b[1].audioOffsetMs ?? 0);
      return offsetDiff !== 0 ? offsetDiff : a[0].localeCompare(b[0]);
    });

  return { baseId, familyEntries };
};
